import { useCallback, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { EmailPage, EmailPageNavKind } from "../components/emails/emailPages";
import { useView } from "../contexts/ViewContext";

/**
 * Bridges the Emails view's open account/sender/conversation to the URL's
 * `?account=`/`?sender=`/`?thread=` params, so a refresh on `/emails`
 * returns to the same email in the same mailbox instead of the senders
 * grid in whichever account happens to load by default. TodoApp calls this
 * once, *before* its own `useGmail()` call, so `initialAccountRef` is ready
 * for `useGmail` to pick up instead of always defaulting to the primary
 * connected account.
 */
export function useEmailPageUrlSync() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches the untyped navigate() used throughout TodoApp for this router's dynamic search shape
  const navigate = useNavigate() as any;
  const { viewType } = useView();
  const { account, sender, thread } = useSearch({ strict: false }) as {
    account?: string;
    sender?: string;
    thread?: string;
  };

  // Read once, at mount, purely to seed useGmail's initial pick — kept as a
  // ref (rather than the live value) so it doesn't change useGmail's
  // `loadConnection` identity, and so doesn't retrigger that one-shot
  // bootstrap, on every URL change. Live back/forward resync reads `account`
  // directly below instead — see `urlAccount`.
  const initialAccountRef = useRef(account ?? null);

  const onEmailPageChange = useCallback(
    (page: EmailPage | null, navKind: EmailPageNavKind = "replace") => {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          sender: page?.senderKey,
          thread: page?.kind === "detail" ? page.threadId : undefined,
        }),
        replace: navKind === "replace",
      });
    },
    [navigate],
  );

  // A deliberate pick of a different mailbox (clicking an account tab)
  // pushes, same as opening a sender or a conversation — it gets its own
  // back-button checkpoint, so Back from account B lands on account A
  // instead of skipping past it to wherever the Emails view was opened
  // from. Stepping via swipe or the ←/→ shortcut still replaces, same as
  // stepping between sibling senders/conversations. Either way, the
  // sender/thread from the old account are cleared outright rather than
  // left for the prune effect to catch, so there's no frame where a stale
  // selection is read against the new account's (still loading) mail.
  const onAccountChange = useCallback(
    (nextAccount: string, navKind: EmailPageNavKind = "replace") => {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          account: nextAccount,
          sender: undefined,
          thread: undefined,
        }),
        replace: navKind === "replace",
      });
    },
    [navigate],
  );

  // Fills in `?account=` once useGmail resolves which mailbox it defaulted
  // to (arriving at /emails with no account named picks the first connected
  // one) — otherwise the URL never names an account until the user
  // explicitly switches one, so a refresh or a copied link silently falls
  // back to whichever account happens to load first instead of the one
  // actually on screen. A `replace`, same as `onAccountChange`, but leaves
  // sender/thread untouched: this isn't a switch, just naming the mailbox
  // already showing.
  //
  // Guarded on `viewType === "emails"`: `useGmail` loads on every view, not
  // just this one, so the resolved-account signal this reacts to fires just
  // as often — writing on the board or the calendar would plant a stray
  // `?account=` in a URL that has nothing to do with mail. `viewType` sits
  // in the dependency list (not just the guard) so this callback gets a new
  // identity the moment the route *becomes* /emails — the signal
  // `useGmailAccountUrlSync` needs to re-check a resolution that already
  // happened while looking at some other view.
  const onAccountResolved = useCallback(
    (resolvedAccount: string) => {
      if (viewType !== "emails") return;
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          account: resolvedAccount,
        }),
        replace: true,
      });
    },
    [navigate, viewType],
  );

  return {
    initialAccountRef,
    // The URL's *current* account, for TodoApp's useGmailAccountUrlSync call
    // (mirrors initialSenderKey/initialThreadId below) — unlike
    // initialAccountRef this is meant to change, so pass it as a plain
    // value rather than a ref.
    urlAccount: account ?? null,
    onAccountChange,
    onAccountResolved,
    // Spread straight onto EmailsViewShell — its prop names match these.
    shellProps: {
      initialSenderKey: sender ?? null,
      initialThreadId: thread ?? null,
      onEmailPageChange,
    },
  };
}
